import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import {Modal} from 'react-native';
import {Trans, useTranslation} from 'react-i18next';
// import {ReceiveButton} from './weekly-card-reward';
import {cn, toPriceStr} from '@/utils';
import ModalReceiveButton from '../components/modal-receive-button';
import {useScreenSize} from '@/common-pages/hooks/size.hooks';
import styles from '../vip.module.scss';
interface WeeklyRewardProps {
  zIndex?: number;
  onClose?: () => void;
}

export interface WeeklyRewardRef {
  show: (data: {
    baseMoney: number;
    multiple: number;
    bonusMoney: number;
  }) => void;
}

const RewardArea = ({
  step,
  baseMoney,
  bonusMoney,
  multiple,
}: {
  step: number;
  baseMoney: number;
  bonusMoney: number;
  multiple: number;
}) => {
  const [nowMoney, setNowMoney] = useState(0);
  const [showBaseMoney, setShowBaseMoney] = useState(false);
  const animationRef = useRef<number>();

  const animateMoney = (start: number, end: number, duration: number) => {
    const startTime = performance.now();

    const _step = (timestamp: number) => {
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setNowMoney(Math.floor(start + (end - start) * progress));

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(_step);
      }
    };

    animationRef.current = requestAnimationFrame(_step);
  };

  useEffect(() => {
    if (step === 1) {
      setNowMoney(baseMoney);
      setShowBaseMoney(false);
    } else {
      animateMoney(baseMoney, bonusMoney, 750); // 1秒内从baseMoney增长到bonusMoney
      setTimeout(() => {
        setShowBaseMoney(true);
      }, 750);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [baseMoney, bonusMoney, step]);
  return (
    <div className="w-[9.375rem] h-[6.625rem] relative overflow-visible">
      <div
        className={cn(
          'absolute -left-1.5 -right-1.5 top-0 -bottom-3 bg-no-repeat bg-full z-[1]',
          !showBaseMoney ? '' : 'invisible',
        )}
        style={{
          backgroundImage: `url(${require('@/assets/imgs/vip/v2/base-reward.webp')})`,
        }}
      />
      <div
        className={cn(
          'absolute -left-1.5 -right-1.5 top-0 -bottom-3.5 bg-no-repeat bg-full z-[1]',
          showBaseMoney ? '' : 'invisible',
        )}
        style={{
          backgroundImage: `url(${require('@/assets/imgs/vip/v2/bonus-reward.webp')})`,
        }}
      />
      <div
        className={cn(
          'absolute -top-3 -right-3 rotate-12 size-[3.5625rem] bg-full bg-no-repeat flex items-center justify-center font-din font-black text-white [text-shadow:0px_2px_0px_#BF0C03] z-[2] text-[1.5rem]',
          !showBaseMoney ? 'invisible' : styles['animate-multi-scaleIn'],
        )}
        style={{
          backgroundImage: `url(${require('@/assets/imgs/vip/v2/multi.webp')})`,
        }}>
        x{multiple}
      </div>
      <div
        className={cn(
          'absolute bottom-0 w-full z-[2] flex flex-col items-center',
          !showBaseMoney ? 'h-[2.0625rem]' : 'h-[2.6875rem]',
        )}>
        <span
          className={cn(
            'text-white text-base font-semibold line-through -mb-3',
            showBaseMoney ? '' : 'hidden',
          )}>
          {toPriceStr(baseMoney, {fixed: 0})}
        </span>
        <span
          className={cn(
            'text-[#FE0] font-semibold text-[1.375rem]',
            styles['vip-weekly-red-text'],
          )}>
          {toPriceStr(nowMoney, {fixed: 0})}
        </span>
      </div>
    </div>
  );
};

const WeeklyReward = forwardRef<WeeklyRewardRef, WeeklyRewardProps>(
  ({zIndex, onClose}, ref) => {
    const [visible, setVisible] = useState(false);
    const [baseMoney, setBaseMoney] = useState(0);
    const [bonusMoney, setBonusMoney] = useState(0);
    const [multiple, setMultiple] = useState(0);
    const [canClickReceive, setCanClickReceive] = useState(false);
    const [step, setStep] = useState(1);
    const modelRef = useRef<Modal>(null);
    const {i18n} = useTranslation();

    useImperativeHandle(ref, () => ({
      show: (data: {
        baseMoney: number;
        multiple: number;
        bonusMoney: number;
      }) => {
        setVisible(true);
        setBaseMoney(data.baseMoney);
        setBonusMoney(data.bonusMoney);
        setMultiple(data.multiple);
        setStep(1);
        setCanClickReceive(true);
      },
    }));

    useEffect(() => {
      if (visible && zIndex != null && modelRef.current) {
        let parent = modelRef.current as unknown as HTMLDivElement;
        while (parent && parent.parentElement !== document.body) {
          parent = parent.parentElement as HTMLDivElement;
        }
        console.log('parent', parent);
        (parent.children[0] as HTMLDivElement).style.zIndex = zIndex + '';
      }
    }, [visible, zIndex]);

    const handleReceive = () => {
      if (step === 1) {
        if (baseMoney >= bonusMoney) {
          setVisible(false);
          onClose?.();
          return;
        }
        setStep(2);
        setCanClickReceive(false);
        setTimeout(() => {
          setCanClickReceive(true);
        }, 1000);
      } else {
        setVisible(false);
        onClose?.();
      }
    };

    const {t} = useTranslation();
    const {screenHeight} = useScreenSize();

    const weeklyRewardsTitleImg = useMemo(() => {
      const lang = i18n.language;
      const imgMap = {
        en_US: require('@/assets/imgs/vip/v2/weekly-rewards-title.webp'),
        hi_IN: require('@/assets/imgs/vip/v2/weekly-rewards-title-hi.webp'),
        te_IN: require('@/assets/imgs/vip/v2/weekly-rewards-title-te.webp'),
        ml_IN: require('@/assets/imgs/vip/v2/weekly-rewards-title-ml.webp'),
        ta_IN: require('@/assets/imgs/vip/v2/weekly-rewards-title-ta.webp'),
      };
      return imgMap[lang as keyof typeof imgMap] || imgMap.en_US;
    }, [i18n]);
    return (
      <Modal
        visible={visible}
        ref={modelRef}
        transparent
        statusBarTranslucent={false}
        animationType="fade">
        <div
          className="w-full h-full bg-black/50"
          onClick={() => {
            handleReceive();
          }}>
          <div
            className={cn(
              'absolute top-0 left-1/2 -translate-x-1/2 w-[23.4375rem] h-[50.75rem] bg-full bg-no-repeat flex flex-col items-center',
              visible
                ? styles['animate-base-modal-show']
                : styles['animate-base-modal-hide'],
              screenHeight < 700 ? '-top-10' : '',
            )}
            onClick={e => e.stopPropagation()}
            style={{
              backgroundImage: `url(${require('@/assets/imgs/vip/v2/weekly-rewards.webp')})`,
            }}>
            <div className="w-[16.9375rem] mt-[12.125rem] flex flex-col items-center">
              <div
                className="w-[16.9375rem] h-[5.6875rem] bg-contain bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url(${weeklyRewardsTitleImg})`,
                }}
              />
              <div className="flex flex-col mt-1.5">
                <p className="text-[#5D0808] text-xs text-center">
                  {t('vip.tip.weekReward.t1')}
                </p>
                <p className="text-[#5D0808] mt-2 text-xs text-center">
                  {t('vip.tip.weekReward.t2')}
                </p>
                <ul className="flex flex-col gap-1 text-[#CB1919] text-xs font-normal list-disc ml-1 mt-2.5 leading-[normal]">
                  <li className="ml-1">{t('vip.tip.weekReward.p1')}</li>
                  <li className="ml-1">
                    <Trans
                      i18nKey="vip.tip.weekReward.p2"
                      components={[
                        <span className="font-semibold text-sm text-[#4502FF]" />,
                      ]}
                      values={{
                        money: toPriceStr(10000, {fixed: 0}),
                      }}
                    />
                  </li>
                </ul>
                <div className="flex flex-col gap-2 items-center mt-5">
                  <p className="text-[#8D3118] text-xs">
                    {t('vip.tip.weekReward.yourReward')}
                  </p>
                  <RewardArea
                    step={step}
                    baseMoney={Math.min(baseMoney, bonusMoney)}
                    bonusMoney={bonusMoney}
                    multiple={multiple}
                  />
                </div>
              </div>
              <ModalReceiveButton
                className="mt-5"
                disabled={!canClickReceive}
                onClick={handleReceive}
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  },
);

export default WeeklyReward;
